'use client';

import { getVariantFromSimpleControls } from '@/utils/animation';
import { getColorScheme } from '@/utils/getColorScheme';
import { getCTA } from '@/utils/getCTA';
import { getVerticalSpacing } from '@/utils/getVerticalSpacing';
import makeId from '@/utils/makeId';
import { normalizeSvgEmbedMarkup } from '@/utils/normalizeSvgEmbed';
import { parseImagePosition } from '@/utils/parseImagePosition';
import { motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import { CompareSlider } from './atoms/CompareSlider';
import VideoEmbed from './atoms/VideoEmbed';

import { MarkDown } from './atoms/MarkDown';

/* ------------------------------ Helpers ------------------------------ */

function valOf(x, fallback = '') {
    if (x == null) return fallback;
    if (typeof x === 'object' && 'value' in x) return x.value ?? fallback;
    return x;
}

function hasMeaningfulText(value) {
    const raw = valOf(value, '');
    if (raw == null) return false;
    const text = String(raw)
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;|&#160;/gi, ' ')
        .replace(/\u00a0/g, ' ')
        .trim();
    return text.length > 0;
}

function featureHasContent(feature) {
    if (!feature || typeof feature !== 'object') return false;
    const icon = String(valOf(feature.Icon, '')).trim();
    return (
        icon.length > 0 ||
        hasMeaningfulText(feature.Title) ||
        hasMeaningfulText(feature.Description)
    );
}

function normalizeAnimation(raw) {
    const v = String(raw ?? '')
        .toLowerCase()
        .trim();
    const map = {
        '': 'slide',
        none: 'none',
        fade: 'fade',
        'fade in': 'fade',
        slide: 'slide',
        'slide up': 'slide',
        zoom: 'zoom',
        'zoom in': 'zoom',
        stagger: 'stagger',
        cascade: 'stagger',
    };
    return map[v] ?? 'slide';
}

function MotionMaybe({ enable, k, motionProps, className, style, children }) {
    if (enable) {
        return (
            <motion.div
                key={`${k}-on`}
                className={className}
                style={style}
                {...motionProps}
            >
                {children}
            </motion.div>
        );
    }
    return (
        <div
            key={`${k}-off`}
            className={className}
            style={{ ...style, opacity: '', transform: '' }}
        >
            {children}
        </div>
    );
}

const isEmbedCode = (val) =>
    typeof val === 'string' &&
    /<\s*(iframe|script|video|svg|div|section|figure|blockquote)[\s>]/i.test(
        val
    );

function normalizeMediaUrl(input) {
    if (!input) return '';
    if (typeof input === 'string') return input.trim();
    if (typeof input === 'object') {
        const candidate =
            input.Media ||
            input.media ||
            input.url ||
            input.src ||
            input.value ||
            '';
        return typeof candidate === 'string' ? candidate.trim() : '';
    }
    return '';
}

function isSelectedMedia(input) {
    const mediaUrl = normalizeMediaUrl(input);
    return Boolean(mediaUrl && mediaUrl !== '/images/placeholder.svg');
}

const SERVICE_NAV_MEDIA_LAYOUT = {
    slim: { top: 96, shift: -40 },
    compact: { top: 108, shift: -28 },
    standard: { top: 132, shift: -4 },
    large: { top: 156, shift: 20 },
};

function normalizeServiceNavHeight(value) {
    const raw = String(value || '')
        .toLowerCase()
        .trim()
        .replace(/[_\s]+/g, '-');
    if (
        raw === 'slim' ||
        raw === 'tight' ||
        raw === 'thin' ||
        raw === 'minimal'
    ) {
        return 'slim';
    }
    if (
        raw === 'large' ||
        raw === 'taller' ||
        raw === 'extra-tall' ||
        raw === 'x-tall'
    ) {
        return 'large';
    }
    if (raw === 'standard' || raw === 'tall') return 'standard';
    return 'compact';
}

function readServiceNavHeightMode() {
    if (typeof document === 'undefined') return 'compact';
    const nav = document.querySelector('[data-component="Nav"]');
    return normalizeServiceNavHeight(
        nav?.dataset?.navHeight ||
            document.documentElement?.dataset?.navHeightMode ||
            'compact'
    );
}

/* -------------------------------- Feature -------------------------------- */

function Feature(props) {
    const {
        Icon,
        Title,
        Description,
        iconColor,
        textColor,
        layout,
        animate = true,
        duration = 0.6,
    } = props;

    const isIconTop = layout === 'icon-top';

    const fadeIn = {
        offscreen: { opacity: 0, y: 12 },
        onscreen: {
            opacity: 1,
            y: 0,
            transition: { type: 'tween', duration: Math.max(0.4, duration) },
        },
    };

    const Inner = isIconTop ? (
        <div className="relative text-center lg:px-5">
            <div className={`inline font-bold ${textColor}`}>
                <div className="block">
                    <i
                        className={`${Icon} mt-5 pb-3 text-2xl ${iconColor} lg:pb-5 lg:text-4xl`}
                    />
                </div>
                <MarkDown
                    variant="h3"
                    className="font-display text-lg leading-6"
                >
                    {Title}
                </MarkDown>
            </div>
            <div>
                <MarkDown className="pb-4">{Description}</MarkDown>
            </div>
        </div>
    ) : (
        <div className="relative">
            <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-8 w-8 shrink-0 items-start justify-center">
                    <i
                        className={`${Icon} text-2xl leading-none ${iconColor}`}
                    />
                </div>
                <div className="min-w-0 flex-1">
                    <div className={`font-bold ${textColor}`}>
                        <MarkDown
                            variant="h3"
                            className="font-display text-lg leading-6"
                        >
                            {Title}
                        </MarkDown>
                    </div>
                    <div>
                        <MarkDown className="pb-4">{Description}</MarkDown>
                    </div>
                </div>
            </div>
        </div>
    );

    if (!animate) return <div>{Inner}</div>;

    return (
        <motion.div
            variants={fadeIn}
            initial="offscreen"
            whileInView="onscreen"
            viewport={{ once: true }}
        >
            {Inner}
        </motion.div>
    );
}

/* -------------------------------- Service -------------------------------- */

export default function Service(props) {
    const {
        Tagline,
        Anchor,
        Header,
        Body,
        Features = [],
        BeforeAltText,
        AfterAltText,
        BeforeImage,
        AfterImage,
        BeforeImagePosition,
        AfterImagePosition,
        ImagePosition,
        StickyMedia = false,
        MediaPosition = '50',
        ColorScheme = 'white',
        ImageVariant = 'cover',
        VerticalSpacing = 'medium',
        Medias = [],

        Animation = 'fade',
        AnimationPreset,
        Duration = 0.6,
        AnimationGroup = 'features',
        EditorPreview = false,
    } = props;

    const visibleFeatures = Array.isArray(Features)
        ? Features.reduce((acc, feature, originalIndex) => {
              if (!featureHasContent(feature)) return acc;
              acc.push({ feature, originalIndex });
              return acc;
          }, [])
        : [];

    /* -------------------------- Animation -------------------------- */

    const preset = normalizeAnimation(Animation ?? AnimationPreset ?? 'slide');

    const [prefersReduced, setPrefersReduced] = useState(false);
    useEffect(() => {
        if (typeof window !== 'undefined' && window.matchMedia) {
            const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
            const update = (e) => setPrefersReduced(e.matches);
            setPrefersReduced(mq.matches);
            mq.addEventListener?.('change', update);
            return () => mq.removeEventListener?.('change', update);
        }
    }, []);

    const isStagger = preset === 'stagger';
    const viewport = { once: !EditorPreview, amount: 0.5 };

    /* --------------------------- Group Selection ---------------------------- */
    const rawGroup = String(AnimationGroup || 'content')
        .toLowerCase()
        .trim();
    const allowed = new Set([
        'features',
        'feature',
        'content',
        'ctas',
        'medias',
        'media',
        'image',
        'images',
        'background',
        'bg',
    ]);
    const group = allowed.has(rawGroup)
        ? rawGroup === 'feature'
            ? 'features'
            : ['media', 'image', 'images', 'background', 'bg'].includes(
                  rawGroup
              )
            ? 'medias'
            : rawGroup
        : 'content';

    const animateMedia = group === 'medias';
    const animateTextBlocks = group === 'content';
    const animateKeyFeatures = group === 'content' || group === 'features';
    const animateCTAs = group === 'content' || group === 'ctas';

    const baseKey = `${group}-${preset}-${Duration}-${
        EditorPreview ? 'preview' : 'prod'
    }`;

    const containerMotionProps = useMemo(() => {
        if (prefersReduced) return {};
        if (isStagger) {
            const variants = getVariantFromSimpleControls('stagger', Duration);
            if (EditorPreview)
                return { variants, initial: 'hidden', animate: 'show' };
            return {
                variants,
                initial: 'hidden',
                whileInView: 'show',
                viewport,
            };
        }
        return {};
    }, [prefersReduced, isStagger, Duration, EditorPreview]);

    const childMotionProps = useMemo(() => {
        if (prefersReduced) return {};
        if (isStagger) {
            const childVariants = {
                hidden: { opacity: 0, y: 16 },
                show: {
                    opacity: 1,
                    y: 0,
                    transition: { duration: Math.max(0.4, Duration - 0.2) },
                },
            };
            return { variants: childVariants };
        }
        if (preset === 'none') return {};
        const v = getVariantFromSimpleControls(preset, Duration);
        const hasOffscreen = v && 'offscreen' in v;
        const initialState = hasOffscreen ? 'offscreen' : 'hidden';
        const targetState = hasOffscreen ? 'onscreen' : 'show';
        if (EditorPreview)
            return { variants: v, initial: initialState, animate: targetState };
        return {
            variants: v,
            initial: initialState,
            whileInView: targetState,
            viewport,
        };
    }, [prefersReduced, isStagger, preset, Duration, EditorPreview]);

    /* --------------------------- Layout + Variants -------------------------- */

    let imagePositionClass, gridCol, textAlignment, uspLayout, featureLayout;

    const mainGridItemsAlignment = ImageVariant.includes('vertical-center')
        ? 'lg:items-center'
        : 'lg:items-start';

    switch (ImagePosition) {
        case 'center':
            imagePositionClass = 'max-w-5xl lg:mx-auto';
            textAlignment = 'text-center';
            uspLayout =
                'grid mx-auto grid-cols-1 lg:grid-cols-3 max-w-xl lg:max-w-full';
            featureLayout = 'icon-top';
            break;
        case 'right':
            imagePositionClass = 'lg:mx-0 max-w-2xl';
            gridCol = 'lg:grid-cols-2';
            uspLayout = 'grid grid-cols-1 space-y-5 max-w-xl';
            featureLayout = 'icon-left';
            break;
        default:
            imagePositionClass = 'lg:order-last lg:mx-0 max-w-2xl';
            gridCol = 'lg:grid-cols-2';
            uspLayout = 'grid grid-cols-1 space-y-5 max-w-xl';
            featureLayout = 'icon-left';
            break;
    }

    const defaultButtonAlignmentClass =
        ImagePosition === 'center'
            ? 'justify-center'
            : 'justify-center md:justify-normal';

    const getImageVariantClasses = (variant) => {
        const isSquare = variant.includes('square');
        const isContain = variant.includes('contain');
        const hasShadow = !variant.includes('no-shadow');
        const isVerticalCenterForText = variant.includes('vertical-center');

        const shape = isSquare
            ? 'rounded-[var(--shape-radius)]'
            : 'rounded-[min(1.5rem,var(--shape-radius))]';
        const shadow = hasShadow ? 'shadow-xl' : '';

        let imageClass = 'object-cover w-full aspect-[16/9] overflow-hidden';
        let sliderClass = 'w-full aspect-[16/9]';
        let sliderStyle;
        if (isContain) {
            imageClass = 'object-contain w-full';
            sliderClass = 'w-full';
        } else if (variant.includes('portrait')) {
            imageClass = 'object-cover aspect-[9/16] w-full mx-auto';
            sliderClass = 'w-full aspect-[9/16]';
        } else if (isSquare) {
            imageClass = 'object-cover h-full w-full';
            sliderClass = 'relative w-full overflow-hidden';
            sliderStyle = { aspectRatio: '1 / 1' };
        }

        const textVerticalCenterClass = isVerticalCenterForText
            ? 'flex-col justify-center lg:my-auto'
            : '';

        return {
            shape,
            shadow,
            imageClass,
            textVerticalCenterClass,
            sliderClass,
            sliderStyle,
        };
    };

    const {
        shape,
        shadow,
        imageClass,
        textVerticalCenterClass,
        sliderClass,
        sliderStyle,
    } = getImageVariantClasses(ImageVariant);

    const {
        taglineColor,
        backgroundColor,
        textColor,
        iconColor,
        thirdButton,
        secondaryButton,
        backgroundStyle,
    } = getColorScheme(ColorScheme, props.GradientDirection);

    const vSpace = getVerticalSpacing(VerticalSpacing);
    const baseMediaPosition = useMemo(
        () => parseImagePosition(MediaPosition),
        [MediaPosition]
    );
    const beforeMediaPosition = useMemo(
        () => parseImagePosition(BeforeImagePosition ?? MediaPosition),
        [BeforeImagePosition, MediaPosition]
    );
    const afterMediaPosition = useMemo(
        () => parseImagePosition(AfterImagePosition ?? MediaPosition),
        [AfterImagePosition, MediaPosition]
    );
    const selectedMedias = useMemo(
        () =>
            Array.isArray(Medias)
                ? Medias.filter((media) => isSelectedMedia(media?.Media))
                : [],
        [Medias]
    );
    const hasBeforeAfter =
        isSelectedMedia(BeforeImage) && isSelectedMedia(AfterImage);
    const hasMediaContent = selectedMedias.length > 0 || hasBeforeAfter;
    const stickyMediaEnabled = String(valOf(StickyMedia, false))
        .trim()
        .toLowerCase();
    const shouldStickMedia =
        hasMediaContent &&
        (stickyMediaEnabled === 'true' || stickyMediaEnabled === '1') &&
        ImagePosition !== 'center';
    const [serviceNavHeightMode, setServiceNavHeightMode] = useState('compact');
    useEffect(() => {
        if (!shouldStickMedia || typeof window === 'undefined') return;

        let frame = 0;
        const updateNavHeightMode = () => {
            window.cancelAnimationFrame(frame);
            frame = window.requestAnimationFrame(() => {
                const nextMode = readServiceNavHeightMode();
                setServiceNavHeightMode((prev) =>
                    prev === nextMode ? prev : nextMode
                );
            });
        };

        updateNavHeightMode();
        window.addEventListener('resize', updateNavHeightMode);

        const observer =
            typeof MutationObserver !== 'undefined'
                ? new MutationObserver(updateNavHeightMode)
                : null;
        if (observer) {
            observer.observe(document.documentElement, {
                attributes: true,
                attributeFilter: ['data-nav-height-mode'],
            });
            if (document.body) {
                observer.observe(document.body, {
                    attributes: true,
                    attributeFilter: ['data-nav-height'],
                    childList: true,
                    subtree: true,
                });
            }
        }

        return () => {
            window.cancelAnimationFrame(frame);
            window.removeEventListener('resize', updateNavHeightMode);
            observer?.disconnect();
        };
    }, [shouldStickMedia]);
    const serviceNavMediaLayout =
        SERVICE_NAV_MEDIA_LAYOUT[serviceNavHeightMode] ||
        SERVICE_NAV_MEDIA_LAYOUT.compact;
    const sectionOverflowClass = shouldStickMedia
        ? 'overflow-hidden lg:overflow-visible'
        : 'overflow-hidden';
    const stickyMediaClass = shouldStickMedia
        ? 'lg:sticky lg:self-start lg:top-[var(--service-sticky-media-top)] lg:mt-[var(--service-sticky-media-shift)]'
        : '';
    const stickyMediaStyle = shouldStickMedia
        ? {
              '--service-sticky-media-top': `${serviceNavMediaLayout.top}px`,
              '--service-sticky-media-shift': `${serviceNavMediaLayout.shift}px`,
              '--service-sticky-media-max-h': `calc(100dvh - ${serviceNavMediaLayout.top}px - ${serviceNavMediaLayout.shift}px - 1.5rem)`,
          }
        : undefined;
    const stickyMediaBlockClass = '';
    const effectiveTextAlignment = hasMediaContent
        ? textAlignment || ''
        : 'text-center';
    const effectiveFeatureLayout = hasMediaContent ? featureLayout : 'icon-top';
    const centeredFeatureCount = visibleFeatures.length;
    const shouldUseCenteredFeatureGrid =
        ImagePosition === 'center' || !hasMediaContent;
    let centeredMediaUspLayout = uspLayout;
    if (shouldUseCenteredFeatureGrid) {
        if (centeredFeatureCount <= 1) {
            centeredMediaUspLayout = 'grid mx-auto grid-cols-1 max-w-xl';
        } else if (centeredFeatureCount === 2) {
            centeredMediaUspLayout =
                'grid mx-auto grid-cols-1 gap-5 md:grid-cols-2 max-w-4xl';
        } else {
            centeredMediaUspLayout =
                'grid mx-auto grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-6 max-w-xl lg:max-w-full';
        }
    }
    const effectiveUspLayout = shouldUseCenteredFeatureGrid
        ? centeredMediaUspLayout
        : uspLayout;
    const gridColClass = hasMediaContent ? gridCol || '' : 'lg:grid-cols-1';
    const shouldCenterTextContentBlock =
        !hasMediaContent || ImagePosition === 'center';
    const buttonAlignmentClass = hasMediaContent
        ? defaultButtonAlignmentClass
        : 'justify-center';
    const getCenteredFeatureItemClass = (index) => {
        if (!shouldUseCenteredFeatureGrid || centeredFeatureCount < 3) {
            return '';
        }

        const remainder = centeredFeatureCount % 3;
        const isLast = index === centeredFeatureCount - 1;
        const isSecondLast = index === centeredFeatureCount - 2;

        if (remainder === 1 && isLast) return 'lg:col-span-2 lg:col-start-3';
        if (remainder === 2 && isSecondLast)
            return 'lg:col-span-2 lg:col-start-2';
        if (remainder === 2 && isLast) return 'lg:col-span-2 lg:col-start-4';

        return 'lg:col-span-2';
    };
    const forceTwoColumnMediaGrid =
        ImagePosition === 'center' && selectedMedias.length >= 2;
    const pairBeforeAfterAndSingleMedia =
        ImagePosition === 'center' &&
        hasBeforeAfter &&
        selectedMedias.length === 1;
    const mediaSectionLayoutClass = pairBeforeAfterAndSingleMedia
        ? 'grid grid-cols-1 gap-4 md:grid-cols-2'
        : 'flex flex-col gap-y-4';

    const { hasAny: hasCTAs, element: CTAs } = getCTA(props, {
        secondaryVariant: secondaryButton,
        outlineVariant: thirdButton,
        buttonAlign: buttonAlignmentClass,
        wrapSpacing: 'mt-10 lg:mt-16',
        enableAnim: animateCTAs && preset !== 'none' && !prefersReduced,
        motionProps: childMotionProps,
    });

    let mediaGridColsClass = '';
    if (forceTwoColumnMediaGrid) {
        mediaGridColsClass = 'grid-cols-2';
    } else if (ImageVariant.includes('portrait') && selectedMedias.length > 0) {
        mediaGridColsClass =
            selectedMedias.length === 1 ? 'grid-cols-1' : 'grid-cols-2';
    } else if (hasBeforeAfter) {
        mediaGridColsClass =
            selectedMedias.length === 1 ? 'grid-cols-1' : 'grid-cols-2';
    } else if (selectedMedias.length === 1) {
        mediaGridColsClass = 'grid-cols-1';
    } else if (selectedMedias.length >= 2) {
        mediaGridColsClass =
            ImageVariant === 'cover' ||
            ImageVariant.includes('landscape') ||
            ImageVariant.includes('square') ||
            ImageVariant.includes('cover-vertical-center')
                ? 'grid-cols-2 sm:grid-cols-2 lg:grid-cols-1'
                : 'grid-cols-2 lg:grid-cols-1';
    }

    return (
        <div
            className={`editable-component relative px-6 lg:px-8 ${sectionOverflowClass} ${backgroundColor} ${vSpace}`}
            style={backgroundStyle}
            data-component="Service"
        >
            {/* Anchor */}
            {Anchor && (
                <div
                    key={Anchor}
                    className="absolute -mt-56"
                    id={makeId(Anchor)}
                />
            )}

            <div className="relative z-40 mx-auto max-w-7xl">
                <div
                    className={`${gridColClass} grid grid-cols-1 gap-12 ${mainGridItemsAlignment}`}
                >
                    {/* Media Section (Slider or Grid) */}
                    {hasMediaContent && (
                        <MotionMaybe
                            enable={
                                !shouldStickMedia &&
                                animateMedia &&
                                preset !== 'none' &&
                                !prefersReduced
                            }
                            k={`${baseKey}-media`}
                            motionProps={childMotionProps}
                            className={`mx-auto w-full ${imagePositionClass} relative ${mediaSectionLayoutClass} ${stickyMediaClass}`}
                            style={stickyMediaStyle}
                        >
                            {/* Before/After Slider */}
                            {hasBeforeAfter && (
                                <div
                                    className={`w-full overflow-hidden lg:max-h-[var(--service-sticky-media-max-h)] ${stickyMediaBlockClass} ${sliderClass} ${shape} ${shadow}`}
                                    style={sliderStyle}
                                >
                                    <CompareSlider
                                        beforeSrc={BeforeImage}
                                        afterSrc={AfterImage}
                                        beforeAlt={BeforeAltText}
                                        afterAlt={AfterAltText}
                                        className="h-full w-full"
                                        imageClass={imageClass}
                                        beforeImageStyle={{
                                            objectPosition: `${beforeMediaPosition.x}% ${beforeMediaPosition.y}%`,
                                        }}
                                        afterImageStyle={{
                                            objectPosition: `${afterMediaPosition.x}% ${afterMediaPosition.y}%`,
                                        }}
                                    />
                                </div>
                            )}

                            {/* Media Grid (Images/Videos) */}
                            {selectedMedias.length > 0 && (
                                <div
                                    className={`grid w-full gap-4 overflow-hidden lg:max-h-[var(--service-sticky-media-max-h)] ${stickyMediaBlockClass} ${
                                        shouldStickMedia
                                            ? 'lg:auto-rows-fr'
                                            : ''
                                    } ${mediaGridColsClass}`}
                                >
                                    {selectedMedias
                                        .slice(0, 2)
                                        .map((media, index) => {
                                            const isEmbed = isEmbedCode(
                                                media.Media
                                            );
                                            const isImage =
                                                /\.(jpg|jpeg|png|svg|webp|gif)$/i.test(
                                                    media.Media
                                                );

                                            let aspectRatioPadding;
                                            if (
                                                ImageVariant.includes(
                                                    'portrait'
                                                )
                                            ) {
                                                aspectRatioPadding = '177.78%';
                                            } else if (
                                                ImageVariant.includes('square')
                                            ) {
                                                aspectRatioPadding = '100%';
                                            } else if (
                                                ImageVariant.includes('cover')
                                            ) {
                                                aspectRatioPadding = '75%';
                                            } else if (
                                                ImageVariant.includes(
                                                    'landscape'
                                                )
                                            ) {
                                                aspectRatioPadding = '56.25%';
                                            } else {
                                                aspectRatioPadding = '56.25%';
                                            }

                                            const mediaPosition =
                                                media?.MediaPosition
                                                    ? parseImagePosition(
                                                          media.MediaPosition
                                                      )
                                                    : baseMediaPosition;
                                            const mediaObjectPosition = `${mediaPosition.x}% ${mediaPosition.y}%`;

                                            if (isEmbed) {
                                                const embedMarkup =
                                                    normalizeSvgEmbedMarkup(
                                                        media.Media,
                                                        {
                                                            x: mediaPosition.x,
                                                            y: mediaPosition.y,
                                                            mode: ImageVariant.includes(
                                                                'contain'
                                                            )
                                                                ? 'contain'
                                                                : 'cover',
                                                        }
                                                    );
                                                return (
                                                    <div
                                                        key={index}
                                                        className="relative mx-auto h-full w-full"
                                                    >
                                                        <div
                                                            className={`relative w-full overflow-hidden ${shape} ${shadow}`}
                                                            style={{
                                                                paddingTop:
                                                                    aspectRatioPadding,
                                                            }}
                                                        >
                                                            <div
                                                                className="absolute inset-0 [&>*]:h-full [&>*]:max-h-none [&>*]:w-full [&>*]:max-w-none [&>svg]:block"
                                                                dangerouslySetInnerHTML={{
                                                                    __html: embedMarkup,
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                );
                                            }

                                            if (isImage) {
                                                return (
                                                    <div
                                                        key={index}
                                                        className="relative mx-auto h-full w-full"
                                                    >
                                                        {ImageVariant.includes(
                                                            'square'
                                                        ) ? (
                                                            <div
                                                                className={`aspect-h-1 aspect-w-1 w-full overflow-hidden ${shape} ${shadow}`}
                                                            >
                                                                <img
                                                                    className="h-full w-full object-cover"
                                                                    style={{
                                                                        objectPosition:
                                                                            mediaObjectPosition,
                                                                    }}
                                                                    src={
                                                                        media.Media
                                                                    }
                                                                    alt={
                                                                        media.AltText ||
                                                                        ''
                                                                    }
                                                                    loading="lazy"
                                                                />
                                                            </div>
                                                        ) : (ImageVariant.includes(
                                                              'cover'
                                                          ) ||
                                                              ImageVariant.includes(
                                                                  'portrait'
                                                              ) ||
                                                              ImageVariant.includes(
                                                                  'landscape'
                                                              )) &&
                                                          !ImageVariant.includes(
                                                              'contain'
                                                          ) ? (
                                                            (() => {
                                                                const isSinglePortraitNonContain =
                                                                    ImageVariant.includes(
                                                                        'portrait'
                                                                    ) &&
                                                                    selectedMedias.length ===
                                                                        1;
                                                                const containerClasses = `relative ${
                                                                    isSinglePortraitNonContain
                                                                        ? 'mx-auto w-full'
                                                                        : 'w-full'
                                                                } overflow-hidden ${shape} ${shadow}`;
                                                                return (
                                                                    <div
                                                                        className={
                                                                            containerClasses
                                                                        }
                                                                        style={{
                                                                            paddingTop:
                                                                                aspectRatioPadding,
                                                                        }}
                                                                    >
                                                                        <img
                                                                            src={
                                                                                media.Media
                                                                            }
                                                                            alt={
                                                                                media.AltText ||
                                                                                ''
                                                                            }
                                                                            loading="lazy"
                                                                            className="absolute left-0 top-0 h-full w-full object-cover"
                                                                            style={{
                                                                                objectPosition:
                                                                                    mediaObjectPosition,
                                                                            }}
                                                                        />
                                                                    </div>
                                                                );
                                                            })()
                                                        ) : (
                                                            <img
                                                                className={`${imageClass} ${shape} ${shadow}`}
                                                                width="1000"
                                                                height="1000"
                                                                src={
                                                                    media.Media
                                                                }
                                                                alt={
                                                                    media.AltText ||
                                                                    ''
                                                                }
                                                                loading="lazy"
                                                                style={{
                                                                    objectPosition:
                                                                        mediaObjectPosition,
                                                                }}
                                                            />
                                                        )}
                                                    </div>
                                                );
                                            }

                                            // Video
                                            const videoShapeClass =
                                                ImageVariant.includes('square')
                                                    ? 'rounded-[var(--shape-radius)]'
                                                    : 'rounded-[min(1.5rem,var(--shape-radius))]';
                                            const wrapperClasses = `relative mx-auto w-full overflow-hidden ${videoShapeClass} ${shadow}`;

                                            return (
                                                <div
                                                    key={index}
                                                    className={wrapperClasses}
                                                    style={{
                                                        paddingTop:
                                                            aspectRatioPadding,
                                                    }}
                                                >
                                                    <VideoEmbed
                                                        url={media.Media}
                                                        controls
                                                        // 👇 ADD THIS CONFIG BLOCK
                                                        config={{
                                                            youtube: {
                                                                playerVars: {
                                                                    autoplay: 0,
                                                                    modestbranding: 1,
                                                                    rel: 0,
                                                                },
                                                            },
                                                        }}
                                                        // 👆 END CONFIG BLOCK
                                                        className="absolute left-0 top-0 h-full w-full"
                                                        width="100%"
                                                        height="100%"
                                                        onError={(err) => {
                                                            // helps confirm if it's YouTube 153 vs something else
                                                            console.error(
                                                                'ReactPlayer error for media',
                                                                media.Media,
                                                                err
                                                            );
                                                        }}
                                                    />
                                                </div>
                                            );
                                        })}
                                </div>
                            )}
                        </MotionMaybe>
                    )}

                    {/* Text Content Section */}
                    <div
                        className={`mx-auto flex w-full ${textVerticalCenterClass} ${effectiveTextAlignment} ${
                            shouldCenterTextContentBlock ? 'justify-center' : ''
                        }`}
                    >
                        <div className={!hasMediaContent ? 'max-w-3xl' : ''}>
                            {/* Tagline */}
                            {Tagline && (
                                <MotionMaybe
                                    enable={
                                        animateTextBlocks &&
                                        preset !== 'none' &&
                                        !prefersReduced
                                    }
                                    k={`${baseKey}-tagline`}
                                    motionProps={childMotionProps}
                                >
                                    <MarkDown
                                        className={`mx-auto max-w-2xl text-base font-semibold uppercase leading-7 tracking-wide ${taglineColor}`}
                                    >
                                        {Tagline}
                                    </MarkDown>
                                </MotionMaybe>
                            )}

                            {/* Header */}
                            {Header && (
                                <MotionMaybe
                                    enable={
                                        animateTextBlocks &&
                                        preset !== 'none' &&
                                        !prefersReduced
                                    }
                                    k={`${baseKey}-header`}
                                    motionProps={childMotionProps}
                                >
                                    <MarkDown
                                        variant="h2"
                                        className={`mx-auto mt-2 max-w-2xl font-display text-2xl tracking-tight md:text-3xl lg:text-4xl ${textColor}`}
                                    >
                                        {Header}
                                    </MarkDown>
                                </MotionMaybe>
                            )}

                            {/* Body */}
                            {Body && (
                                <MotionMaybe
                                    enable={
                                        animateTextBlocks &&
                                        preset !== 'none' &&
                                        !prefersReduced
                                    }
                                    k={`${baseKey}-body`}
                                    motionProps={childMotionProps}
                                >
                                    <MarkDown
                                        className={`mx-auto mt-6 max-w-2xl text-base font-text leading-7 md:text-lg ${textColor}`}
                                    >
                                        {Body}
                                    </MarkDown>
                                </MotionMaybe>
                            )}

                            {/* Features */}
                            {visibleFeatures.length > 0 && (
                                <MotionMaybe
                                    enable={
                                        animateKeyFeatures &&
                                        isStagger &&
                                        !prefersReduced &&
                                        preset !== 'none'
                                    }
                                    k={`${baseKey}-features-container`}
                                    motionProps={containerMotionProps}
                                    className={`mt-10 ${effectiveUspLayout} text-sm font-text leading-6 md:text-base md:leading-7 ${textColor}`}
                                >
                                    <div className="contents">
                                        {visibleFeatures.map(
                                            (
                                                { feature, originalIndex },
                                                index
                                            ) => {
                                                const centeredFeatureItemClass =
                                                    getCenteredFeatureItemClass(
                                                        index
                                                    );

                                                return isStagger &&
                                                    animateKeyFeatures &&
                                                    !prefersReduced &&
                                                    preset !== 'none' ? (
                                                    <motion.div
                                                        key={`${
                                                            feature?.Title ??
                                                            'feature'
                                                        }-${originalIndex}-${baseKey}`}
                                                        className={
                                                            centeredFeatureItemClass
                                                        }
                                                        variants={{
                                                            hidden: {
                                                                opacity: 0,
                                                                y: 16,
                                                            },
                                                            show: {
                                                                opacity: 1,
                                                                y: 0,
                                                                transition: {
                                                                    duration:
                                                                        Math.max(
                                                                            0.4,
                                                                            Duration -
                                                                                0.2
                                                                        ),
                                                                },
                                                            },
                                                        }}
                                                    >
                                                        <Feature
                                                            {...feature}
                                                            iconColor={
                                                                iconColor
                                                            }
                                                            layout={
                                                                effectiveFeatureLayout
                                                            }
                                                            textColor={
                                                                textColor
                                                            }
                                                            animate={false}
                                                            duration={Duration}
                                                        />
                                                    </motion.div>
                                                ) : (
                                                    // Non-stagger path
                                                    <MotionMaybe
                                                        key={`${
                                                            feature?.Title ??
                                                            'feature'
                                                        }-${originalIndex}-${baseKey}`}
                                                        enable={
                                                            animateKeyFeatures &&
                                                            !isStagger &&
                                                            preset !== 'none' &&
                                                            !prefersReduced
                                                        }
                                                        k={`${baseKey}-feature-${originalIndex}`}
                                                        className={
                                                            centeredFeatureItemClass
                                                        }
                                                        motionProps={
                                                            childMotionProps
                                                        }
                                                    >
                                                        <Feature
                                                            {...feature}
                                                            iconColor={
                                                                iconColor
                                                            }
                                                            layout={
                                                                effectiveFeatureLayout
                                                            }
                                                            textColor={
                                                                textColor
                                                            }
                                                            animate={false}
                                                            duration={Duration}
                                                        />
                                                    </MotionMaybe>
                                                );
                                            }
                                        )}
                                    </div>
                                </MotionMaybe>
                            )}

                            {/* Buttons (CTAs) */}
                            {hasCTAs && CTAs}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
